// ============================================================
// ADMIN PROFILE PAGE
// ============================================================

import React, { useState } from 'react';
import { User, Save, Shield, Clock, Eye, EyeOff } from 'lucide-react';
import { Button, Avatar, Alert } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const AdminProfile = () => {
  const { user } = useAuth();
  const [showPass, setShowPass] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || 'Biryani Admin',
    email: user?.email || 'admin@biryanihub.com',
    phone: '+91 98765 43210',
    designation: 'Super Administrator',
    department: 'Platform Operations',
  });
  const [pwd, setPwd] = useState({ current: '', newPass: '', confirm: '' });

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const setP = k => e => setPwd(p => ({ ...p, [k]: e.target.value }));

  const saveProfile = () => {
    setSaved(true);
    toast.success('Profile updated!');
    setTimeout(() => setSaved(false), 3000);
  };

  const changePassword = () => {
    if (!pwd.current) { toast.error('Enter current password'); return; }
    if (pwd.newPass.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwd.newPass !== pwd.confirm) { toast.error('Passwords do not match'); return; }
    toast.success('Password changed successfully!');
    setPwd({ current: '', newPass: '', confirm: '' });
  };

  const activityLog = [
    { action: 'Logged in', time: '2 minutes ago', ip: '192.168.1.1' },
    { action: 'Approved restaurant verification', time: '1 hour ago', ip: '192.168.1.1' },
    { action: 'Updated commission rate', time: '3 hours ago', ip: '192.168.1.1' },
    { action: 'Resolved support ticket #1234', time: '5 hours ago', ip: '192.168.1.2' },
    { action: 'Exported settlement report', time: '1 day ago', ip: '192.168.1.1' },
    { action: 'Created delivery zone', time: '2 days ago', ip: '192.168.1.1' },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Admin Profile</h1>
          <p className="admin-page-subtitle">Manage your account settings and security</p>
        </div>
      </div>

      {saved && <Alert type="success" message="Profile saved successfully!" style={{ marginBottom: 20 }} />}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 24 }}>
        {/* Profile Card */}
        <div>
          <div className="admin-card" style={{ marginBottom: 20 }}>
            <div className="admin-card-body" style={{ textAlign: 'center', padding: 32 }}>
              <Avatar name={form.name} size={80} style={{ margin: '0 auto 16px', fontSize: 28 }} />
              <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>{form.name}</h2>
              <p style={{ margin: '0 0 8px', color: '#94A3B8', fontSize: 14 }}>{form.designation}</p>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(232,93,4,0.1)',
                color: '#E85D04', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700
              }}>
                <Shield size={12} />Super Admin
              </span>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header"><h3 className="admin-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} />Recent Activity</h3></div>
            <div className="admin-card-body" style={{ padding: 0 }}>
              {activityLog.map((a, i) => (
                <div key={i} style={{ padding: '12px 16px', borderBottom: i < activityLog.length - 1 ? '1px solid var(--admin-border)' : 'none' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{a.action}</div>
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{a.time} · IP: {a.ip}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Edit Forms */}
        <div>
          <div className="admin-card" style={{ marginBottom: 20 }}>
            <div className="admin-card-header"><h3 className="admin-card-title"><User size={14} style={{ marginRight: 6 }} />Personal Information</h3></div>
            <div className="admin-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                {[['Full Name', 'name'], ['Email Address', 'email'], ['Phone', 'phone'], ['Designation', 'designation'], ['Department', 'department']].map(([label, key]) => (
                  <div key={key} className="admin-form-group">
                    <label>{label}</label>
                    <input className="admin-form-control" value={form[key]} onChange={set(key)} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Button variant="primary" icon={<Save size={14} />} onClick={saveProfile}>Save Profile</Button>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header"><h3 className="admin-card-title"><Shield size={14} style={{ marginRight: 6 }} />Change Password</h3></div>
            <div className="admin-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                {[['Current Password', 'current'], ['New Password', 'newPass'], ['Confirm New Password', 'confirm']].map(([label, key]) => (
                  <div key={key} className="admin-form-group">
                    <label>{label}</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPass ? 'text' : 'password'} className="admin-form-control" value={pwd[key]} onChange={setP(key)} style={{ paddingRight: 40 }} />
                      <button onClick={() => setShowPass(p => !p)} style={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8',
                      }}>{showPass ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Button variant="primary" icon={<Shield size={14} />} onClick={changePassword}>Change Password</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
